import { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useData } from '../../../contexts/DataContext';
import { colors, LoadingSpinner, Card, Button, EmptyState } from '../../shared';
import { Settings, UserPlus, Trash2, Shield, Check, X, Eye, EyeOff } from 'lucide-react';

// POV Selector Component - allows admin to "view as" a specific technician
const PovSelector = () => {
  const { technicians, povTechId, setPovTech, getCurrentTech } = useData();
  const currentTech = getCurrentTech();
  const isPovActive = !!povTechId;

  // Filter to individual technicians only (exclude multi-tech combo entries from AppFolio)
  const individualTechs = technicians
    .filter(t => t.name && !t.name.includes(','))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Card className="mb-6" style={{ border: isPovActive ? `2px solid ${colors.orange}` : `1px solid ${colors.border}` }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: isPovActive ? colors.orangeDim : colors.yellowDim }}>
            {isPovActive ? <Eye size={18} style={{ color: colors.orange }} /> : <EyeOff size={18} style={{ color: colors.yellow }} />}
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: colors.textPrimary }}>
              View as Technician (POV Mode)
            </h3>
            <p className="text-xs" style={{ color: colors.textMuted }}>
              {isPovActive
                ? `Currently viewing as: ${currentTech?.name || povTechId}`
                : 'Select a technician to see the app from their perspective'}
            </p>
          </div>
        </div>
        {isPovActive && (
          <Button variant="secondary" size="sm" onClick={() => setPovTech(null)}>
            <EyeOff size={14} />
            Exit POV
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <select
          value={povTechId || ''}
          onChange={(e) => setPovTech(e.target.value || null)}
          className="flex-1 px-4 py-2 rounded-xl outline-none"
          style={{
            background: colors.inputBg,
            color: colors.textPrimary,
            border: `1px solid ${isPovActive ? colors.orange : colors.border}`
          }}
        >
          <option value="">-- Select a technician --</option>
          {individualTechs.map(tech => (
            <option key={tech.id} value={tech.id}>
              {tech.name} {tech.email ? `(${tech.email})` : ''}
            </option>
          ))}
        </select>
      </div>

      {isPovActive && (
        <div className="mt-3 p-3 rounded-lg" style={{ background: colors.orangeDim }}>
          <p className="text-sm" style={{ color: colors.orange }}>
            <strong>POV Mode Active:</strong> You are viewing the app as {currentTech?.name}.
            Work orders, receipts, and other data will be filtered to this technician's view.
          </p>
        </div>
      )}
    </Card>
  );
};

const PERMISSION_OPTIONS = [
  { key: 'viewPrompts', label: 'View Prompts', description: 'Can view AI prompts and their configurations' },
  { key: 'editPrompts', label: 'Edit Prompts', description: 'Can create and modify prompts' },
  { key: 'deployPrompts', label: 'Deploy Prompts', description: 'Can deploy prompt changes to production' },
  { key: 'viewAnalytics', label: 'View Analytics', description: 'Can view usage analytics and metrics' },
  { key: 'viewFeedback', label: 'View Feedback', description: 'Can view user feedback on AI responses' },
  { key: 'manageAdmins', label: 'Manage Admins', description: 'Can add/remove admins and modify permissions' },
];

const AdminCard = ({ admin, onUpdate, onDelete, currentUserEmail, canManage }) => {
  const [editing, setEditing] = useState(false);
  const [permissions, setPermissions] = useState(admin.permissions || {});
  const [saving, setSaving] = useState(false);

  const isCurrentUser = admin.email === currentUserEmail;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(admin.email, permissions);
      setEditing(false);
    } catch (error) {
      console.error('Error updating admin:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-3" style={{ border: `1px solid ${colors.border}` }}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium" style={{ color: colors.textPrimary }}>{admin.email}</p>
            {isCurrentUser && (
              <span className="px-2 py-0.5 rounded text-xs" style={{ background: colors.yellowDim, color: colors.yellow }}>
                You
              </span>
            )}
          </div>
          <p className="text-xs" style={{ color: colors.textMuted }}>
            Added {admin.addedAt?.toDate?.().toLocaleDateString() || 'Unknown'}
            {admin.addedBy && ` by ${admin.addedBy}`}
          </p>
        </div>
        {canManage && !isCurrentUser && (
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="p-2 rounded-lg"
                  style={{ background: colors.greenDim, color: colors.green }}
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="p-2 rounded-lg"
                  style={{ background: colors.cardBg, color: colors.textMuted }}
                >
                  <X size={16} />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="p-2 rounded-lg"
                  style={{ background: colors.cardBg, color: colors.textMuted }}
                >
                  <Shield size={16} />
                </button>
                <button
                  onClick={() => onDelete(admin.email)}
                  className="p-2 rounded-lg"
                  style={{ background: colors.redDim, color: colors.red }}
                >
                  <Trash2 size={16} />
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          {PERMISSION_OPTIONS.map(opt => (
            <label
              key={opt.key}
              className="flex items-center gap-3 p-2 rounded-lg cursor-pointer"
              style={{ background: colors.inputBg }}
            >
              <input
                type="checkbox"
                checked={permissions[opt.key] || false}
                onChange={(e) => setPermissions({ ...permissions, [opt.key]: e.target.checked })}
                className="w-4 h-4"
              />
              <div>
                <p className="text-sm" style={{ color: colors.textPrimary }}>{opt.label}</p>
                <p className="text-xs" style={{ color: colors.textMuted }}>{opt.description}</p>
              </div>
            </label>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {PERMISSION_OPTIONS.filter(opt => admin.permissions?.[opt.key]).map(opt => (
            <span
              key={opt.key}
              className="px-2 py-1 rounded text-xs"
              style={{ background: colors.inputBg, color: colors.textSecondary }}
            >
              {opt.label}
            </span>
          ))}
          {!Object.values(admin.permissions || {}).some(v => v) && (
            <span className="text-xs" style={{ color: colors.textMuted }}>No permissions</span>
          )}
        </div>
      )}
    </Card>
  );
};

export default function SettingsTab({ permissions, user }) {
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPermissions, setNewAdminPermissions] = useState({
    viewPrompts: true,
    viewAnalytics: true,
    viewFeedback: true,
  });
  const [adding, setAdding] = useState(false);

  const canManage = permissions?.manageAdmins;

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'admin_whitelist'));
      const adminsData = snapshot.docs.map(doc => ({ email: doc.id, ...doc.data() }));
      setAdmins(adminsData);
    } catch (error) {
      console.error('Error loading admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminEmail.trim() || !newAdminEmail.includes('@')) return;

    setAdding(true);
    try {
      await setDoc(doc(db, 'admin_whitelist', newAdminEmail.toLowerCase()), {
        email: newAdminEmail.toLowerCase(),
        addedAt: Timestamp.now(),
        addedBy: user?.email,
        permissions: newAdminPermissions,
      });
      await loadAdmins();
      setShowAddAdmin(false);
      setNewAdminEmail('');
      setNewAdminPermissions({
        viewPrompts: true,
        viewAnalytics: true,
        viewFeedback: true,
      });
    } catch (error) {
      console.error('Error adding admin:', error);
    } finally {
      setAdding(false);
    }
  };

  const handleUpdateAdmin = async (email, newPermissions) => {
    await setDoc(doc(db, 'admin_whitelist', email), {
      permissions: newPermissions,
    }, { merge: true });
    await loadAdmins();
  };

  const handleDeleteAdmin = async (email) => {
    if (!window.confirm(`Remove ${email} from admin whitelist?`)) return;
    await deleteDoc(doc(db, 'admin_whitelist', email));
    await loadAdmins();
  };

  if (!canManage) {
    return (
      <div className="p-6">
        <EmptyState
          icon={Settings}
          title="Access Denied"
          description="You don't have permission to manage admin settings"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: colors.textPrimary }}>Settings</h1>
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            Manage admin access and permissions
          </p>
        </div>
        <Button onClick={() => setShowAddAdmin(true)}>
          <UserPlus size={18} />
          Add Admin
        </Button>
      </div>

      {/* POV Selector */}
      <PovSelector />

      {/* Add Admin Modal */}
      {showAddAdmin && (
        <Card className="mb-6" style={{ border: `1px solid ${colors.yellow}` }}>
          <h3 className="font-semibold mb-4" style={{ color: colors.textPrimary }}>Add New Admin</h3>

          <div className="mb-4">
            <label className="block text-sm mb-2" style={{ color: colors.textSecondary }}>Email</label>
            <input
              type="email"
              value={newAdminEmail}
              onChange={(e) => setNewAdminEmail(e.target.value)}
              placeholder="admin@example.com"
              className="w-full px-4 py-2 rounded-xl outline-none"
              style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm mb-2" style={{ color: colors.textSecondary }}>Permissions</label>
            <div className="space-y-2">
              {PERMISSION_OPTIONS.map(opt => (
                <label
                  key={opt.key}
                  className="flex items-center gap-3 p-2 rounded-lg cursor-pointer"
                  style={{ background: colors.inputBg }}
                >
                  <input
                    type="checkbox"
                    checked={newAdminPermissions[opt.key] || false}
                    onChange={(e) => setNewAdminPermissions({ ...newAdminPermissions, [opt.key]: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <div>
                    <p className="text-sm" style={{ color: colors.textPrimary }}>{opt.label}</p>
                    <p className="text-xs" style={{ color: colors.textMuted }}>{opt.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <Button onClick={handleAddAdmin} disabled={adding || !newAdminEmail.trim()}>
              {adding ? 'Adding...' : 'Add Admin'}
            </Button>
            <Button variant="secondary" onClick={() => setShowAddAdmin(false)}>
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {/* Admin List */}
      <h2 className="text-lg font-semibold mb-4" style={{ color: colors.textPrimary }}>
        Admin Users ({admins.length})
      </h2>

      {admins.length === 0 ? (
        <EmptyState
          icon={Settings}
          title="No admins configured"
          description="Add the first admin to get started"
        />
      ) : (
        <div>
          {admins.map(admin => (
            <AdminCard
              key={admin.email}
              admin={admin}
              onUpdate={handleUpdateAdmin}
              onDelete={handleDeleteAdmin}
              currentUserEmail={user?.email}
              canManage={canManage}
            />
          ))}
        </div>
      )}
    </div>
  );
}
